import React, { useEffect, useState, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
interface PaymentLocation {
  country: string;
  countryCode: string;
  amount: number;
  transactions: number;
  lat: number;
  lng: number;
}
interface GeoPaymentMapProps {
  data: PaymentLocation[];
}
export function GeoPaymentMap({
  data
}: GeoPaymentMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<L.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (mapRef.current && !leafletMap.current) {
      // Initialize map
      leafletMap.current = L.map(mapRef.current, {
        center: [20, 0],
        zoom: 2,
        minZoom: 2,
        maxZoom: 6,
        zoomControl: true
      });
      // Add OpenStreetMap tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(leafletMap.current);
      // Make sure to cleanup on unmount
      return () => {
        if (leafletMap.current) {
          leafletMap.current.remove();
          leafletMap.current = null;
        }
      };
    }
  }, []);
  useEffect(() => {
    if (!leafletMap.current || !data || data.length === 0) return;
    setIsLoading(true);
    setError(null);
    // Clear existing layers (except tile layer)
    leafletMap.current.eachLayer(layer => {
      if (layer instanceof L.GeoJSON || layer instanceof L.Marker || layer instanceof L.Circle) {
        leafletMap.current?.removeLayer(layer);
      }
    });
    // Calculate total payment amount for percentage calculation
    const totalAmount = data.reduce((sum, location) => sum + location.amount, 0);
    // Function to get color based on percentage of total payment volume
    const getColor = (amount: number) => {
      const percentage = amount / totalAmount * 100;
      if (percentage > 20) return '#0a3d62'; // Very dark blue
      if (percentage > 15) return '#1e5d8c';
      if (percentage > 10) return '#3282b8';
      if (percentage > 5) return '#4fa6d7';
      if (percentage > 2) return '#6ec1e4';
      if (percentage > 1) return '#88d1ed';
      if (percentage > 0) return '#bbdef0';
      return '#f1f1f1'; // Light gray for countries with no data
    };
    // Create a mapping of country codes to payment data
    // Use both alpha-2 and alpha-3 codes for better matching
    const countryData: Record<string, PaymentLocation> = {};
    const countryNameMap: Record<string, PaymentLocation> = {};
    data.forEach(location => {
      // Store by country code (both uppercase and lowercase for safety)
      countryData[location.countryCode.toUpperCase()] = location;
      countryData[location.countryCode.toLowerCase()] = location;
      // Also store by country name (normalized to lowercase)
      countryNameMap[location.country.toLowerCase()] = location;
      // Add common country code variants
      if (location.countryCode === 'GB') {
        countryData['UK'] = location; // Some datasets use UK instead of GB
      }
      if (location.countryCode === 'US') {
        countryData['USA'] = location; // Some datasets use USA instead of US
      }
    });
    // Create fallback circles for all locations regardless of GeoJSON loading
    data.forEach(location => {
      const marker = L.circle([location.lat, location.lng], {
        color: '#3282b8',
        fillColor: getColor(location.amount),
        fillOpacity: 0.7,
        radius: Math.sqrt(location.amount) * 10000
      }).addTo(leafletMap.current!);
      marker.bindPopup(`
        <strong>${location.country}</strong><br/>
        Transactions: ${location.transactions.toLocaleString()}<br/>
        Volume: $${location.amount.toLocaleString()}<br/>
        Percentage: ${(location.amount / totalAmount * 100).toFixed(1)}%
      `);
    });
    // Try multiple GeoJSON sources in case one fails
    const geoJsonUrls = ['https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson', 'https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson', 'https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json'];
    // Function to attempt loading GeoJSON from a URL
    const loadGeoJson = (url: string, index: number) => {
      fetch(url).then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch GeoJSON: ${response.statusText}`);
        }
        return response.json();
      }).then(geoData => {
        // Verify GeoJSON data structure
        if (!geoData.features || !Array.isArray(geoData.features)) {
          throw new Error('Invalid GeoJSON format');
        }
        // Create GeoJSON layer
        const geoJsonLayer = L.geoJSON(geoData, {
          style: feature => {
            // Try different property paths to find country code
            // Different GeoJSON sources use different property structures
            const iso2 = feature.properties?.iso_a2 || feature.properties?.ISO_A2;
            const iso3 = feature.properties?.iso_a3 || feature.properties?.ISO_A3;
            const name = feature.properties?.name || feature.properties?.NAME || feature.properties?.ADMIN;
            // Find matching payment data using different identifiers
            let location = null;
            if (iso2 && countryData[iso2.toUpperCase()]) {
              location = countryData[iso2.toUpperCase()];
            } else if (iso3 && countryData[iso3.toUpperCase()]) {
              location = countryData[iso3.toUpperCase()];
            } else if (name && countryNameMap[name.toLowerCase()]) {
              location = countryNameMap[name.toLowerCase()];
            }
            return {
              fillColor: location ? getColor(location.amount) : '#f1f1f1',
              weight: 1,
              opacity: 0.8,
              color: 'white',
              fillOpacity: 0.7
            };
          },
          onEachFeature: (feature, layer) => {
            // Try different property paths to find country info
            const iso2 = feature.properties?.iso_a2 || feature.properties?.ISO_A2;
            const iso3 = feature.properties?.iso_a3 || feature.properties?.ISO_A3;
            const name = feature.properties?.name || feature.properties?.NAME || feature.properties?.ADMIN;
            // Find matching payment data
            let location = null;
            if (iso2 && countryData[iso2.toUpperCase()]) {
              location = countryData[iso2.toUpperCase()];
            } else if (iso3 && countryData[iso3.toUpperCase()]) {
              location = countryData[iso3.toUpperCase()];
            } else if (name && countryNameMap[name.toLowerCase()]) {
              location = countryNameMap[name.toLowerCase()];
            }
            if (location) {
              layer.bindPopup(`
                  <strong>${location.country}</strong><br/>
                  Transactions: ${location.transactions.toLocaleString()}<br/>
                  Volume: $${location.amount.toLocaleString()}<br/>
                  Percentage: ${(location.amount / totalAmount * 100).toFixed(1)}%
                `);
            } else {
              layer.bindPopup(`<strong>${name || 'Unknown'}</strong><br/>No payment data`);
            }
          }
        }).addTo(leafletMap.current!);
        // Add legend
        const legend = L.control({
          position: 'bottomright'
        });
        legend.onAdd = () => {
          const div = L.DomUtil.create('div', 'info legend');
          div.style.backgroundColor = 'white';
          div.style.padding = '6px 8px';
          div.style.border = '1px solid #ccc';
          div.style.borderRadius = '4px';
          div.style.lineHeight = '18px';
          div.style.color = '#555';
          const grades = [0, 1, 2, 5, 10, 15, 20];
          const colors = ['#f1f1f1', '#bbdef0', '#88d1ed', '#6ec1e4', '#4fa6d7', '#3282b8', '#1e5d8c', '#0a3d62'];
          div.innerHTML += '<h4 style="margin:0 0 5px 0;">Payment Volume</h4>';
          // Loop through the intervals and generate a label with a colored square for each interval
          for (let i = 0; i < grades.length; i++) {
            div.innerHTML += `<i style="display:inline-block; width:18px; height:18px; background:${colors[i]}; margin-right:8px"></i> ` + `${grades[i]}%` + (grades[i + 1] ? ` &ndash; ${grades[i + 1]}%<br>` : '+<br>');
          }
          return div;
        };
        legend.addTo(leafletMap.current!);
        setIsLoading(false);
        setError(null);
      }).catch(err => {
        console.error(`Error loading GeoJSON from ${url}:`, err);
        // Try the next URL if available
        if (index < geoJsonUrls.length - 1) {
          loadGeoJson(geoJsonUrls[index + 1], index + 1);
        } else {
          // All URLs failed, show error but keep the circles
          setError('Could not load detailed map data. Showing approximate locations.');
          setIsLoading(false);
        }
      });
    };
    // Start with the first URL
    loadGeoJson(geoJsonUrls[0], 0);
  }, [data]);
  return <div className="relative h-full w-full" data-id="element-831">
      <div ref={mapRef} className="h-full w-full" data-id="element-832" />
      {isLoading && <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50" data-id="element-833">
          <div className="text-blue-600 font-medium" data-id="element-834">Loading map data...</div>
        </div>}
      {error && <div className="absolute bottom-0 left-0 right-0 bg-red-100 text-red-700 p-2 text-center" data-id="element-835">
          {error}
        </div>}
    </div>;
}